import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Add, Delete, PersonAdd } from '@mui/icons-material';
import { BetaAccessService, BetaUser } from '../services/betaAccessService';
import { useUser } from '../contexts/UserContext';
import { safeFormatDate } from '../utils/dateUtils';

const BetaAccess: React.FC = () => {
  const { user, profile } = useUser();
  const [betaUsers, setBetaUsers] = useState<BetaUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    loadBetaUsers();
  }, []);

  const loadBetaUsers = async () => {
    setLoading(true);
    try {
      const users = await BetaAccessService.getAllBetaUsers();
      setBetaUsers(users);
    } catch (err) {
      console.error('Error loading beta users:', err);
      setError('Failed to load beta users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!newEmail || !newEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      await BetaAccessService.addBetaUser(
        newEmail,
        user?.email || 'unknown',
        notes
      );
      setDialogOpen(false);
      setNewEmail('');
      setNotes('');
      await loadBetaUsers();
    } catch (err) {
      console.error('Error adding beta user:', err);
      setError('Failed to add beta user');
    }
  };

  const handleRemoveUser = async (email: string) => {
    setDeleting(email);
    try {
      await BetaAccessService.removeBetaUser(email);
      await loadBetaUsers();
    } catch (err) {
      console.error('Error removing beta user:', err);
      setError('Failed to remove beta user');
    } finally {
      setDeleting(null);
    }
  };

  if (!profile?.isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You do not have permission to access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Beta Access Management</Typography>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => setDialogOpen(true)}
        >
          Add Beta User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Added By</TableCell>
                <TableCell>Added Date</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : betaUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No beta users found
                  </TableCell>
                </TableRow>
              ) : (
                betaUsers.map((betaUser) => (
                  <TableRow key={betaUser.email}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {betaUser.email}
                        {betaUser.email === user?.email && (
                          <Chip label="You" size="small" color="primary" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{betaUser.addedBy}</TableCell>
                    <TableCell>
                      {safeFormatDate(betaUser.addedAt, 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>{betaUser.notes || '-'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveUser(betaUser.email)}
                        disabled={deleting === betaUser.email}
                      >
                        {deleting === betaUser.email ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Delete />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Beta User</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="user@example.com"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Notes (Optional)"
            fullWidth
            variant="outlined"
            multiline
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g., Beta tester, Friend, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddUser} variant="contained">
            Add User
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BetaAccess;